import { UPLOAD_PURPOSE_TENANT_LOGO } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import {
  putToSignedUrl,
  stageMemoryPreflightUpload,
  type UploadConfirmResult,
  type UploadPresignResult,
} from "@/features/preflight/api/uploads.api";

export interface TenantLogoUploadConfirmResult extends UploadConfirmResult {
  publicUrl?: string;
}

export async function presignTenantLogoUpload(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_TENANT_LOGO,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

export async function confirmTenantLogoUpload(input: {
  tenantId: string;
  uploadId: string;
}): Promise<TenantLogoUploadConfirmResult> {
  const { data } = await authFetch<TenantLogoUploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function runTenantLogoUploadFlow(input: {
  tenantId: string;
  file: File;
}): Promise<TenantLogoUploadConfirmResult> {
  const presign = await presignTenantLogoUpload({
    tenantId: input.tenantId,
    contentType: input.file.type,
    sizeBytes: input.file.size,
    originalName: input.file.name,
  });

  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType: input.file.type,
      file: input.file,
    });
  } else {
    await putToSignedUrl(presign.uploadUrl, input.file, presign.requiredHeaders);
  }

  return confirmTenantLogoUpload({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
  });
}
